// Per-device rate limiting via localStorage timestamps.
//
// This is the client-side half of the production-hardening rate-limit story.
// It is NOT a security boundary on its own — anyone can clear localStorage —
// but it stops casual abuse and forced-loop bugs from spamming Supabase.
// Edge Functions enforce the server-side counterpart.
//
// Limits documented in PRODUCTION_HARDENING.md:
//   - signup            : 5 per hour
//   - password reset    : 3 per 10 minutes
//   - video upload      : 5 per minute

interface Limit {
  storageKey: string;
  windowMs: number;
  maxAttempts: number;
}

const LIMITS = {
  signup: {
    storageKey: "sportsbio_rl_signup",
    windowMs: 60 * 60 * 1000,    // 1 hour
    maxAttempts: 5,
  },
  passwordReset: {
    storageKey: "sportsbio_rl_pwreset",
    windowMs: 10 * 60 * 1000,    // 10 minutes
    maxAttempts: 3,
  },
  videoUpload: {
    storageKey: "sportsbio_rl_upload",
    windowMs: 60 * 1000,         // 1 minute
    maxAttempts: 5,
  },
} satisfies Record<string, Limit>;

const readTimestamps = (key: string): number[] => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((n) => typeof n === "number") : [];
  } catch {
    return [];
  }
};

const writeTimestamps = (key: string, timestamps: number[]): void => {
  try { localStorage.setItem(key, JSON.stringify(timestamps)); } catch { /* ignore */ }
};

const pruneAndCheck = (limit: Limit): { allowed: boolean; recent: number[]; retryAfterMs: number } => {
  const now = Date.now();
  const recent = readTimestamps(limit.storageKey).filter((t) => now - t < limit.windowMs);
  const allowed = recent.length < limit.maxAttempts;
  // Earliest pruned timestamp + window = next time the user can try.
  const retryAfterMs = allowed ? 0 : Math.max(0, recent[0] + limit.windowMs - now);
  return { allowed, recent, retryAfterMs };
};

const record = (limit: Limit): void => {
  const { recent } = pruneAndCheck(limit);
  recent.push(Date.now());
  writeTimestamps(limit.storageKey, recent);
};

// ---- Public API ---------------------------------------------------------

export const canAttemptSignup = (): boolean => pruneAndCheck(LIMITS.signup).allowed;
export const recordSignupAttempt = (): void => record(LIMITS.signup);

export const canAttemptPasswordReset = (): {
  allowed: boolean;
  retryAfterMs: number;
} => {
  const { allowed, retryAfterMs } = pruneAndCheck(LIMITS.passwordReset);
  return { allowed, retryAfterMs };
};
export const recordPasswordResetAttempt = (): void => record(LIMITS.passwordReset);

export const canAttemptVideoUpload = (): {
  allowed: boolean;
  retryAfterMs: number;
} => {
  const { allowed, retryAfterMs } = pruneAndCheck(LIMITS.videoUpload);
  return { allowed, retryAfterMs };
};
export const recordVideoUploadAttempt = (): void => record(LIMITS.videoUpload);

export const formatRetryAfter = (ms: number): string => {
  if (ms < 1000) return "a moment";
  const seconds = Math.ceil(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.ceil(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  const hours = Math.ceil(minutes / 60);
  return `${hours} hour${hours === 1 ? "" : "s"}`;
};
